import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

import { mapTemplateResponseToSavedTemplate } from '@/api/template-api';
import { BuildWorkspace } from '@/components/build-workspace/build-workspace';
import { TemplateSkeleton } from '@/components/build-workspace/template-skeleton';
import { QueryError } from '@/components/query-error';
import { useTemplate } from '@/hooks/use-templates';

/**
 * `/templates/:templateId` — the edit-existing-template flow.
 * Loads the saved template via `useTemplate`, surfaces a
 * `QueryError` on failure and a `TemplateSkeleton` while the request
 * is in flight, then hands the mapped `SavedTemplate` to the
 * Build workspace. Back returns the user to the landing page.
 */
export function TemplateEditPage() {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const query = useTemplate(templateId ?? null);
  const saved = useMemo(
    () => (query.data ? mapTemplateResponseToSavedTemplate(query.data) : null),
    [query.data],
  );

  if (query.isError) {
    return <QueryError error={query.error} onRetry={() => query.refetch()} />;
  }
  if (query.isLoading || !saved) {
    return <TemplateSkeleton />;
  }

  return (
    <BuildWorkspace
      initialTemplate={saved}
      onBack={(publishedTemplateId) =>
        navigate(
          '/',
          publishedTemplateId ? { state: { activatedId: publishedTemplateId } } : undefined,
        )
      }
    />
  );
}
